import { z } from 'zod';
import { AdminActionInput, ModerationAction, ReportState } from '../shared.js';
import { CursorQuery } from '../../common/pagination.js';
import { TierKey } from '../../subscription.js';
import { Category1, Category2 } from '../../taxonomy.js';
// ── moderation (FR-ADM-20…26) ─────────────────────────────────────────────
export const ReportTargetType = z.enum(['content', 'comment', 'review', 'community', 'user']);
export const ReportReason = z.enum([
    'spam',
    'harassment',
    'hate',
    'misinformation',
    'violence',
    'nudity',
    'impersonation',
    'other',
]);
export const FileReportInput = z.object({
    targetType: ReportTargetType,
    targetId: z.string(),
    reason: ReportReason,
    details: z.string().max(1_000).optional(),
});
export const ReportView = z.object({
    id: z.string(),
    targetType: ReportTargetType,
    targetId: z.string(),
    reason: ReportReason,
    details: z.string().nullable(),
    reporterId: z.string(),
    state: ReportState,
    assigneeId: z.string().nullable(),
    /** 24-hour SLA (NFR-12, AC-ADM-20.2). */
    slaDueAt: z.coerce.date(),
    reportCount: z.number().int(),
    createdAt: z.coerce.date(),
});
/** Everything the moderator needs beside a report (docs/03 §26.4 drawer, AC-ADM-23.1). */
export const ReportContext = z.object({
    target: z.union([
        z.object({
            kind: z.literal('content'),
            id: z.string(),
            type: z.string(),
            title: z.string(),
            body: z.string().nullable(),
            authorId: z.string(),
            status: z.string(),
            category1: z.string(),
            category2: z.string(),
            publishedAt: z.coerce.date().nullable(),
            engagement: z.record(z.string(), z.number()),
        }),
        z.object({
            kind: z.literal('comment'),
            id: z.string(),
            contentId: z.string(),
            channel: z.string(),
            authorId: z.string(),
            body: z.string().nullable(),
            hidden: z.boolean(),
            createdAt: z.coerce.date(),
        }),
        z.object({
            kind: z.literal('review'),
            id: z.string(),
            businessId: z.string(),
            authorId: z.string(),
            rating: z.number(),
            body: z.string(),
            createdAt: z.coerce.date(),
        }),
        z.object({
            kind: z.literal('community'),
            id: z.string(),
            name: z.string(),
            visibility: z.string(),
            memberCount: z.number(),
            createdBy: z.string(),
        }),
        z.object({ kind: z.literal('user'), id: z.string() }),
        z.object({ kind: z.literal('missing'), id: z.string() }),
    ]),
    /** The member behind the target — the one a sanction would hit. */
    author: z
        .object({
        id: z.string(),
        displayName: z.string().nullable(),
        tier: TierKey,
        accountAgeDays: z.number().int(),
        priorSanctions: z.number().int(),
        reportsAgainst: z.number().int(),
        reportRate30d: z.number().int(),
    })
        .nullable(),
    /** Every report on this target, all reporters. */
    reports: z.array(z.object({
        id: z.string(),
        reporterId: z.string(),
        reason: z.string(),
        details: z.string().nullable(),
        state: z.string(),
        createdAt: z.coerce.date(),
    })),
    /** Reporter behaviour signal — mass reporting is itself an abuse vector (US-ADM-27). */
    reporterStats: z.array(z.object({ reporterId: z.string(), reportsFiled30d: z.number().int() })),
});
export const ReportQueueQuery = CursorQuery.extend({
    state: ReportState.optional(),
    targetType: ReportTargetType.optional(),
    reason: ReportReason.optional(),
    category1: Category1.optional(),
    category2: Category2.optional(),
    assigneeId: z.string().optional(),
    /** Nearest-breach first by default (AC-ADM-20.3). */
    sort: z.enum(['sla', 'newest']).default('sla'),
});
export const ModerateInput = AdminActionInput.extend({
    action: ModerationAction,
    suspendDays: z.number().int().min(1).max(365).optional(),
});
export const SanctionView = z.object({
    userId: z.string(),
    suspendedUntil: z.coerce.date().nullable(),
    banned: z.boolean(),
    reason: z.string().nullable(),
});
//# sourceMappingURL=schemas.js.map